import { ChevronDown, Search } from 'lucide-react';
import { useLingui } from '@lingui/react/macro';
import { cn } from '@worldcoo/ui/lib/utils';
import type { CategoryKey } from '@worldcoo/shared/core';
import { CATEGORY_ORDER, useCategoryLabels } from '@/lib/category-labels';
import type { NgoFilters } from './use-partner-ngos';
import type { FilterOption } from './ngo-filter-options';

/** Not `''`, which a blank option could carry; mapped back to `null` as it leaves the control. */
const ANY = '__any__';

function NgoSearch(props: { value: string; onChange: (value: string) => void }) {
	const { t } = useLingui();
	return (
		<div className="relative w-full lg:w-[396px]">
			<input
				type="search"
				value={props.value}
				onChange={(event) => props.onChange(event.target.value)}
				placeholder={t`Search NGOs...`}
				aria-label={t`Search NGOs by name`}
				className="text-ink placeholder:text-ink/60 focus-visible:ring-ink w-full rounded-lg bg-white py-3 pr-11 pl-4 text-base tracking-[-0.02em] focus-visible:ring-2 focus-visible:outline-none [&::-webkit-search-cancel-button]:appearance-none"
			/>
			<Search aria-hidden className="text-ink pointer-events-none absolute top-1/2 right-4 size-4 -translate-y-1/2" />
		</div>
	);
}

/**
 * A bare native `<select>` for the phone's own list: shadcn `Select` is themed for the product UI, and
 * the kit's `NativeSelect` hardcodes `border-input` and a 32px height.
 */
function FilterSelect(props: {
	label: string;
	value: string | null;
	options: FilterOption[];
	onChange: (value: string | null) => void;
	className?: string;
}) {
	if (props.options.length < 2) return null;

	return (
		<div className={cn('relative w-full lg:w-fit', props.className)}>
			<select
				aria-label={props.label}
				value={props.value ?? ANY}
				onChange={(event) => props.onChange(event.target.value === ANY ? null : event.target.value)}
				className="border-ink text-ink focus-visible:ring-ink w-full cursor-pointer appearance-none rounded-lg border bg-transparent py-3 pr-11 pl-6 text-base leading-[1.5] focus-visible:ring-2 focus-visible:outline-none"
			>
				{/* The filter's own name, as the hand-off shows: "Todas las categorías" read as a value and truncated on mobile. */}
				<option value={ANY}>{props.label}</option>
				{props.options.map((option) => (
					<option key={option.value} value={option.value}>
						{option.label}
					</option>
				))}
			</select>
			<ChevronDown
				aria-hidden
				className="text-ink pointer-events-none absolute top-1/2 right-5 size-4 -translate-y-1/2"
			/>
		</div>
	);
}

export function NgoFiltersBar(props: {
	filters: NgoFilters;
	onChange: (filters: NgoFilters) => void;
	countryOptions: FilterOption[];
	regionOptions: FilterOption[];
}) {
	const { t } = useLingui();
	const categoryLabels = useCategoryLabels();
	const set = <K extends keyof NgoFilters>(key: K, value: NgoFilters[K]) =>
		props.onChange({ ...props.filters, [key]: value });

	return (
		<div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between lg:gap-4">
			<NgoSearch value={props.filters.query} onChange={(value) => set('query', value)} />

			{/* A grid, not `flex-wrap`, which breaks on a long country name. */}
			<div className="grid grid-cols-2 gap-4 lg:flex lg:gap-4">
				<FilterSelect
					label={t`Country`}
					value={props.filters.country}
					options={props.countryOptions}
					onChange={(value) => props.onChange({ ...props.filters, country: value, region: null })}
				/>
				<FilterSelect
					label={t`Region`}
					value={props.filters.region}
					options={props.regionOptions}
					onChange={(value) => set('region', value)}
				/>
				<FilterSelect
					label={t`Category`}
					value={props.filters.category}
					options={CATEGORY_ORDER.map((key) => ({ value: key, label: categoryLabels[key] }))}
					onChange={(value) => set('category', value as CategoryKey | null)}
					className="col-span-2 lg:col-span-1"
				/>
			</div>
		</div>
	);
}
